/**
 * A tiny, dependency-free inline sparkline — no new chart-library dependency
 * (DIRECTION.md §1.1/§1.6): the vitals hero's NAV sparkline and the fleet
 * cards' per-organism NAV sparkline both use this. Pure geometry only (no
 * DOM) so the scaling math is unit-testable; callers wrap the returned path
 * in an `<svg>` themselves.
 *
 * Never fabricates a trend from too little data: fewer than two finite
 * points returns `null` (never a guessed flat line, never a synthetic
 * extrapolation) — callers render an honest "not enough history yet" state
 * in that case, same rule every other panel in this app follows.
 */

export interface SparklineGeometry {
  readonly path: string;
  readonly width: number;
  readonly height: number;
  /** Position of the final (most recent) point — useful for a small "you
   * are here" dot at the sparkline's end. */
  readonly lastX: number;
  readonly lastY: number;
  /** `"up"` when the last value is above the first, `"down"` when below,
   * `"flat"` when equal (or within floating-point noise) — callers use this
   * to tone the line/dot, same up/down/flat vocabulary as
   * `src/format/number.ts`. */
  readonly trend: "up" | "down" | "flat";
}

/** Plenty for a sparkline a few dozen pixels wide — more points than this
 * are visually indistinguishable at that size anyway. Windowing here (not
 * just trusting callers to pre-slice) is what keeps `minOf`/`maxOf` below
 * safe: an organism's full `equityCurve()` history is unbounded (weeks of
 * cycles at a 30-60s cadence), and passing that whole array through a
 * spread-based `Math.min(...values)` throws `RangeError: Maximum call
 * stack size exceeded` once it's large enough — a real crash this module
 * had before this window + explicit-loop combination. */
const MAX_POINTS = 120;

function minOf(values: readonly number[]): number {
  let min = values[0]!;
  for (const v of values) if (v < min) min = v;
  return min;
}

function maxOf(values: readonly number[]): number {
  let max = values[0]!;
  for (const v of values) if (v > max) max = v;
  return max;
}

/** Builds an SVG path `d` string for `values` (chronological order, oldest
 * first) scaled to fit exactly inside `width`x`height`, with `padding`
 * pixels of headroom so the line never touches the very top/bottom edge.
 * Non-finite values are dropped (never plotted as 0 or a broken segment);
 * an oversized series is windowed to its most recent `MAX_POINTS` values
 * (still the real, most-recent data — never resampled/averaged/faked) —
 * if fewer than two finite values remain after that, returns `null`. */
export function buildSparkline(
  values: readonly number[],
  width: number,
  height: number,
  padding = 2,
): SparklineGeometry | null {
  const allFinite = values.filter((v) => Number.isFinite(v));
  const finite = allFinite.length > MAX_POINTS ? allFinite.slice(-MAX_POINTS) : allFinite;
  if (finite.length < 2) return null;

  const min = minOf(finite);
  const max = maxOf(finite);
  const span = max - min;
  const innerHeight = Math.max(1, height - padding * 2);
  const stepX = finite.length > 1 ? (width - padding * 2) / (finite.length - 1) : 0;

  const points = finite.map((v, i) => {
    const x = padding + stepX * i;
    // A flat series (span === 0) draws a horizontal mid-line rather than
    // dividing by zero.
    const fraction = span === 0 ? 0.5 : (v - min) / span;
    const y = padding + (1 - fraction) * innerHeight;
    return { x, y };
  });

  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(" ");

  const first = finite[0]!;
  const last = finite[finite.length - 1]!;
  const trend: SparklineGeometry["trend"] = last > first ? "up" : last < first ? "down" : "flat";

  const lastPoint = points[points.length - 1]!;
  return { path, width, height, lastX: lastPoint.x, lastY: lastPoint.y, trend };
}
